import JSZip from 'jszip';
import { XMLParser } from 'fast-xml-parser';
import type { DetectedPlugin } from '../types';

/**
 * PreSonus Studio One .song / .multitrack parser.
 *
 * Studio One project files are ZIP archives containing XML.
 * The Song.xml (or song root XML) contains <Instrument> and <Insert> nodes
 * with a `deviceName` attribute.
 */

function collectFromXmlNode(node: unknown, results: DetectedPlugin[]): void {
  if (!node || typeof node !== 'object') return;

  if (Array.isArray(node)) {
    for (const item of node) collectFromXmlNode(item, results);
    return;
  }

  const obj = node as Record<string, unknown>;

  // Nodes that carry plugin identity in Studio One XML
  const deviceName = obj['@_deviceName'] as string | undefined;
  const uid = obj['@_uid'] as string | undefined; // vendor:name UID pattern

  if (deviceName) {
    // uid is typically "Vendor/PluginName" or "pluginName"
    let name = deviceName;
    let vendor: string | undefined;

    if (uid) {
      const parts = uid.split('/');
      if (parts.length === 2) {
        vendor = parts[0].trim();
        name = parts[1].trim();
      }
    }

    results.push({
      name,
      vendor,
      pluginType: 'VST2', // Studio One can use VST2/VST3/AU; we default VST2 without more info
      confidence: 0.82,
      detectionMethod: 'structured',
    });
  }

  for (const value of Object.values(obj)) {
    collectFromXmlNode(value, results);
  }
}

export async function parseStudioOne(
  bytes: Uint8Array
): Promise<DetectedPlugin[]> {
  const zip = await JSZip.loadAsync(bytes);
  const plugins: DetectedPlugin[] = [];

  const parser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: '@_',
    parseAttributeValue: false,
  });

  for (const [, file] of Object.entries(zip.files)) {
    if (file.dir) continue;
    const name = file.name.toLowerCase();
    if (!name.endsWith('.xml') && !name.endsWith('.song') && name !== 'song') {
      continue;
    }

    try {
      const content = await file.async('string');
      const parsed = parser.parse(content) as unknown;
      collectFromXmlNode(parsed, plugins);
    } catch {
      // Non-XML or unreadable file — skip
    }
  }

  return plugins;
}
